import {USER_KEYS, useUpdateUserNotificationPreferencesMutation} from "@/api/userApi";
import CompanyLogoPlaceholder from "@/assets/light_gradient/company_logo_placeholder.svg";
import Toggle from "@/components/Common/forms/Toggle";
import Loader from "@/components/Common/kpi/Loader";
import type {NotificationPreferenceListSerializer} from "@/generated/Company";
import {useGenericInfiniteQuery} from "@/hooks/useQueryHooks";
import {useSelectedCompany, useUserId} from "@/hooks/useUserHooks";
import {useEffect, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";

const AccountNotificationPreferences: FC = () => {
  const {t} = useTranslation("multi_account");
  const userId = useUserId();
  const selectedCompany = useSelectedCompany();

  const notificationPreferencesQuery = useGenericInfiniteQuery<NotificationPreferenceListSerializer>({
    queryKey: USER_KEYS.qUserNotificationPreferences(userId),
    enabled: !!userId,
    refetchOnMount: true,
  });
  const isNotificationEnabled = useMemo(
    () =>
      notificationPreferencesQuery.data?.find(preference => preference.company_profile.id === selectedCompany?.id)
        ?.email_enabled ?? false,
    [notificationPreferencesQuery.data, selectedCompany?.id],
  );

  const {mutate: updatePreferences, isPending: isUpdatingPreferences} = useUpdateUserNotificationPreferencesMutation({
    companyId: selectedCompany?.id!,
    isInitiallyEnabled: isNotificationEnabled,
    userId,
  });

  useEffect(() => {
    if (notificationPreferencesQuery.hasNextPage && !notificationPreferencesQuery.isFetchingNextPage)
      notificationPreferencesQuery.fetchNextPage();
  }, [
    notificationPreferencesQuery.data,
    notificationPreferencesQuery.hasNextPage,
    notificationPreferencesQuery.isFetchingNextPage,
  ]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="border-soft-grey-500 size-10 shrink-0 rounded-full border">
          <img
            src={selectedCompany?.picture320 ?? selectedCompany?.picture ?? CompanyLogoPlaceholder}
            className="size-full rounded-full object-fill"
          />
        </div>
        <span className="text-neutral-black font-semibold">{selectedCompany?.company_name}</span>
      </div>
      {notificationPreferencesQuery.isPending ? (
        <Loader />
      ) : (
        <div className="flex flex-col gap-3">
          <span className="text-neutral-grey-dark text-sm font-medium">{t("receive_email_notifications")}</span>
          <Toggle
            disabled={isUpdatingPreferences}
            checked={isNotificationEnabled}
            onChange={() => updatePreferences()}
          />
        </div>
      )}
    </div>
  );
};

export default AccountNotificationPreferences;
